/**
 * Frequently Bought Together - Smart recommendations
 * Priority:
 *   1. Shopify Recommendations API (based on real purchase history)
 *   2. Referrer collection (the collection customer browsed before clicking this product)
 *   3. Product's own collections (fallback)
 * All strategies filter out same design (design_asin) and similar titles (>70% overlap)
 */
class FrequentlyBoughtTogether extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productsContainer = this.querySelector('[data-fbt-products]');
    this.template = this.querySelector('[data-fbt-template]');
    this.summary = this.querySelector('[data-fbt-summary]');
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.addAllBtn = this.querySelector('[data-fbt-add-all]');
    this.maxProducts = 2;
    this.productTitle = this.dataset.productTitle || '';
    this.designAsin = this.dataset.designAsin || '';
    this.referrerCollection = this.dataset.referrerCollection || '';

    try {
      this.productCollections = JSON.parse(this.dataset.productCollections || '[]');
    } catch {
      this.productCollections = [];
    }

    if (!this.productsContainer || !this.template) return;

    this.currentItem = this.querySelector('.fbt__item--current');
    this.loadRecommendations();
    if (this.addAllBtn) this.addAllBtn.addEventListener('click', () => this.addAllToCart());
    this.listenVariantChange();
  }

  listenVariantChange() {
    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (!variant || !this.currentItem) return;
        this.updateCurrentItem(variant);
      });
    }
    const variantInput = document.querySelector('product-form input.product-variant-id');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        const variantId = variantInput.value;
        this.currentItem?.setAttribute('data-variant-id', variantId);
        this.fetchVariant(variantId);
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  async fetchVariant(variantId) {
    try {
      const r = await fetch(`${window.shopUrl}/products/${this.dataset.productHandle || ''}.js`);
      const product = await r.json();
      const variant = product.variants.find(v => String(v.id) === String(variantId));
      if (variant) this.updateCurrentItem(variant);
    } catch {}
  }

  updateCurrentItem(variant) {
    if (!this.currentItem) return;
    this.currentItem.dataset.variantId = variant.id;
    this.currentItem.dataset.price = variant.price;
    const priceEl = this.currentItem.querySelector('.fbt__price');
    if (priceEl) {
      const compareHtml = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="fbt__compare-price">${this.formatMoney(variant.compare_at_price)}</s> `
        : '';
      priceEl.innerHTML = compareHtml + this.formatMoney(variant.price);
    }
    if (variant.featured_image) {
      const img = this.currentItem.querySelector('.fbt__image');
      if (img) {
        img.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_300x300$1');
        img.alt = variant.featured_image.alt || variant.title;
      }
    }
    this.updateTotal();
  }

  /** Check if a product should be filtered out */
  shouldFilter(product, currentId, existingIds) {
    if (product.id === currentId) return true;
    if (existingIds.has(product.id)) return true;
    // Same design
    if (this.designAsin) {
      const tags = product.tags || [];
      const tagList = Array.isArray(tags) ? tags : (typeof tags === 'string' ? tags.split(', ') : []);
      if (tagList.some(t => t === 'design_asin:' + this.designAsin)) return true;
    }
    // Similar title (>70% keyword overlap)
    if (this.titleOverlap(this.productTitle, product.title || '') > 0.7) return true;
    return false;
  }

  /** Calculate keyword overlap ratio between two titles (0-1) */
  titleOverlap(titleA, titleB) {
    const stopWords = new Set([
      't-shirt', 'tshirt', 'shirt', 'hoodie', 'sweatshirt', 'unisex', 'men', 'women',
      'for', 'the', 'and', 'with', 'from', 'gift', 'gifts', 'funny', 'cute', 'cool',
      'vintage', 'retro', 'premium', 'graphic', 'design', 'style',
    ]);
    const extract = (t) =>
      t.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
        .filter(w => w.length > 2 && !stopWords.has(w));
    const wordsA = extract(titleA);
    const wordsB = new Set(extract(titleB));
    if (wordsA.length === 0) return 0;
    return wordsA.filter(w => wordsB.has(w)).length / wordsA.length;
  }

  /** Deterministic shuffle using product ID as seed */
  shuffleWithSeed(arr) {
    const seed = parseInt(this.productId) || 0;
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.abs((seed * (i + 1) * 2654435761) | 0) % (i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /** Fetch products from a collection, filtered and shuffled */
  async fetchFromCollection(handle, currentId, existingIds) {
    try {
      const r = await fetch(`${window.shopUrl}/collections/${handle}/products.json?sort_by=best-selling&limit=15`);
      const data = await r.json();
      const candidates = (data.products || []).filter(p => !this.shouldFilter(p, currentId, existingIds));
      return this.shuffleWithSeed(candidates);
    } catch {
      return [];
    }
  }

  async loadRecommendations() {
    const currentId = parseInt(this.productId);
    const products = [];
    const existingIds = new Set();

    const addProducts = (candidates) => {
      for (const c of candidates) {
        if (products.length >= this.maxProducts) break;
        if (!existingIds.has(c.id)) {
          products.push(c);
          existingIds.add(c.id);
        }
      }
    };

    // ── Strategy 1: Shopify Recommendations API (real purchase history) ──
    try {
      const r = await fetch(`${window.shopUrl}/recommendations/products.json?product_id=${this.productId}&limit=10&intent=related`);
      const data = await r.json();
      const candidates = (data.products || []).filter(p => !this.shouldFilter(p, currentId, existingIds));
      addProducts(candidates);
    } catch {}

    // ── Strategy 2: Referrer collection (where customer came from) ──
    if (products.length < this.maxProducts && this.referrerCollection) {
      const candidates = await this.fetchFromCollection(this.referrerCollection, currentId, existingIds);
      addProducts(candidates);
    }

    // ── Strategy 3: Product's own collections (fallback) ──
    if (products.length < this.maxProducts) {
      const skipHandles = new Set(['new-arrivals', 'best-sellers', 'all', 'frontpage']);
      // Don't re-fetch referrer collection
      if (this.referrerCollection) skipHandles.add(this.referrerCollection);
      const nicheCollections = this.productCollections.filter(h => !skipHandles.has(h));
      for (const handle of nicheCollections) {
        if (products.length >= this.maxProducts) break;
        const candidates = await this.fetchFromCollection(handle, currentId, existingIds);
        addProducts(candidates);
      }
    }

    if (products.length === 0) return;

    // Enrich products with variant data if needed
    const enriched = await Promise.all(products.map(async (p) => {
      if (p.variants && p.variants.length > 0 && p.variants[0].id) return p;
      try {
        const handle = p.handle || p.url?.split('/products/')?.[1]?.split('?')[0];
        if (!handle) return p;
        const r = await fetch(`${window.shopUrl}/products/${handle}.js`);
        const full = await r.json();
        return { ...p, variants: full.variants, price: full.price, images: full.images };
      } catch { return p; }
    }));

    this.renderProducts(enriched);
  }

  renderProducts(products) {
    products.forEach((product) => {
      const clone = this.template.content.cloneNode(true);
      const item = clone.querySelector('[data-fbt-item]');
      const img = clone.querySelector('[data-fbt-img]');
      const title = clone.querySelector('[data-fbt-title]');
      const price = clone.querySelector('[data-fbt-price]');

      const variant = product.variants?.[0] || {};
      item.dataset.variantId = variant.id || product.id;
      const rawPrice = variant.price || product.price || 0;
      const priceInCents = typeof rawPrice === 'string'
        ? Math.round(parseFloat(rawPrice) * 100)
        : rawPrice;
      item.dataset.price = priceInCents;

      const productUrl = product.url || `/products/${product.handle}`;
      const imgSrc = product.featured_image?.url || product.featured_image || product.image || product.images?.[0]?.src || '';
      if (imgSrc) {
        img.src = imgSrc.replace(/(\.[^.]+)$/, '_300x300$1');
        img.alt = product.title || '';
      }

      const link = clone.querySelector('[data-fbt-link]');
      if (link) link.href = productUrl;

      const t = product.title || '';
      title.textContent = t.length > 55 ? t.substring(0, 52) + '...' : t;
      title.href = productUrl;
      price.textContent = this.formatMoney(priceInCents);

      this.productsContainer.appendChild(clone);
    });

    if (this.summary) this.summary.removeAttribute('hidden');
    this.updateTotal();
    this.querySelectorAll('.fbt__checkbox').forEach((cb) =>
      cb.addEventListener('change', () => this.updateTotal())
    );
  }

  updateTotal() {
    let total = 0, count = 0;
    this.querySelectorAll('[data-fbt-item]').forEach((item) => {
      const cb = item.querySelector('.fbt__checkbox');
      if (cb && (cb.checked || cb.disabled)) {
        total += parseInt(item.dataset.price || 0, 10);
        count++;
      }
    });
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);
    if (this.addAllBtn) this.addAllBtn.disabled = count < 2;
    const countEl = this.querySelector('[data-fbt-count]');
    if (countEl) countEl.textContent = count > 0 ? `${count} item${count > 1 ? 's' : ''} selected` : '';
  }

  async addAllToCart() {
    const items = [];
    this.querySelectorAll('[data-fbt-item]').forEach((item) => {
      const cb = item.querySelector('.fbt__checkbox');
      if (cb && (cb.checked || cb.disabled)) {
        items.push({ id: parseInt(item.dataset.variantId, 10), quantity: 1 });
      }
    });
    if (items.length === 0) return;

    this.addAllBtn.disabled = true;
    this.addAllBtn.textContent = 'Adding...';
    try {
      const r = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (r.ok) {
        const cartData = await r.json();
        this.addAllBtn.textContent = 'Added!';

        if (window.PUB_SUB_EVENTS?.cartUpdate && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fbt', cartData });
        }

        const cartCount = document.querySelector('cart-icon-bubble');
        if (cartCount) {
          fetch('/cart.js').then(r => r.json()).then(cart => {
            const bubble = cartCount.querySelector('.cart-count-bubble span');
            if (bubble) bubble.textContent = cart.item_count;
            const bubbleWrap = cartCount.querySelector('.cart-count-bubble');
            if (bubbleWrap && cart.item_count > 0) bubbleWrap.removeAttribute('hidden');
          });
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          fetch('/cart?sections=cart-drawer')
            .then(r => r.json())
            .then(sections => {
              const html = sections['cart-drawer'];
              if (html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newDrawer = doc.querySelector('cart-drawer');
                if (newDrawer) {
                  cartDrawer.innerHTML = newDrawer.innerHTML;
                  cartDrawer.classList.remove('is-empty');
                }
              }
              if (cartDrawer.open) cartDrawer.open();
              else if (cartDrawer.classList) { cartDrawer.classList.add('active', 'animate'); }
            });
        }

        setTimeout(() => { this.addAllBtn.textContent = 'Add Selected to Cart'; this.addAllBtn.disabled = false; }, 2500);
      } else throw new Error();
    } catch {
      this.addAllBtn.textContent = 'Error - Try Again';
      setTimeout(() => { this.addAllBtn.textContent = 'Add Selected to Cart'; this.addAllBtn.disabled = false; }, 2000);
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }
}

customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
